import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@radix-ui/react-icons";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as React from "react";
// import { buttonVariants } from "./button";
import { LuChevronsUpDown } from "react-icons/lu";
import { inputVariants } from "./input";
import type { ControlSizeProp } from "./control-size";
import { CustomLabel } from "@/components/common/customLabel";

import { cn } from "@/lib/utils";
import { useContext, useEffect, useRef, useState } from "react";
import { Spinner } from "./spinner";

import { Button } from "./button";
// Define the props interface
interface SelectProps {
  children: React.ReactNode; // Explicitly type children
  [key: string]: unknown; // Allow other props
  onValueChange: (value: string) => void; // Accept both string and number
  value: string | undefined; // Accept both string and number, and undefined for no selection
  options?: { value: string; label?: string }[]; // Add options prop for navigation
  showNavArrows?: boolean; // Add prop to show/hide nav arrows
}

// Define the Select component
const Select: React.FC<SelectProps> = ({
  children,
  showNavArrows = false,
  // options is kept for legacy, but not used for nav
  value,
  onValueChange,
  ...props
}) => {
  const [hasBeenOpened, setHasBeenOpened] = useState(false); // Track if opened at least once
  const [items, setItems] = useState<string[]>([]);
  const isMountedRef = useRef(true);

  const register = (value: string, disabled?: boolean) => {
    if (!disabled)
      setItems((prev) => (prev.includes(value) ? prev : [...prev, value]));
  };
  const unregister = (value: string) => {
    setItems((prev) => prev.filter((v) => v !== value));
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setHasBeenOpened(true); // Set to true when opened
    }
    // Call original onOpenChange if provided
    if (props.onOpenChange && typeof props.onOpenChange === "function") {
      props.onOpenChange(open);
    }
  };

  // CRITICAL: Close select when component unmounts to prevent portal race conditions
  // This is a known issue with Radix UI Portals and Next.js navigation
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Close any open selects from this component instance
      // Use requestAnimationFrame to ensure DOM is still accessible
      requestAnimationFrame(() => {
        if (!isMountedRef.current && typeof document !== "undefined") {
          const triggers = document.querySelectorAll<HTMLElement>(
            "[data-radix-select-trigger][aria-expanded='true']",
          );
          triggers.forEach((trigger) => {
            try {
              trigger.click();
            } catch {
              // Silently ignore - trigger may already be removed
            }
          });
        }
      });
    };
  }, []);

  return (
    <SelectValueChangeContext.Provider value={onValueChange}>
      <SelectItemsContext.Provider value={{ items, register, unregister }}>
        <div className="relative">
          <SelectPrimitive.Root
            onOpenChange={handleOpenChange}
            value={value}
            onValueChange={onValueChange}
            {...props}
          >
            {React.Children.map(children, (child) => {
              if (
                React.isValidElement(child) &&
                (child.type as { displayName?: string }).displayName ===
                  "SelectTrigger"
              ) {
                return React.cloneElement(
                  child as React.ReactElement<
                    Record<string, unknown>,
                    React.JSXElementConstructor<Record<string, unknown>>
                  >,
                  {
                    hasBeenOpened,
                    showNavArrows,
                    value,
                  },
                );
              }
              return child;
            })}
          </SelectPrimitive.Root>
        </div>
      </SelectItemsContext.Provider>
    </SelectValueChangeContext.Provider>
  );
};

const SelectGroup = SelectPrimitive.Group;

// Create a context for the error state
const SelectErrorContext = React.createContext({
  error: false,
  touched: false,
});

// Create contexts before using them
const SelectValueChangeContext = React.createContext<(value: string) => void>(
  () => {},
);

const SelectItemsContext = React.createContext<{
  items: string[];
  register: (value: string, disabled?: boolean) => void;
  unregister: (value: string) => void;
}>({
  items: [],
  register: () => {},
  unregister: () => {},
});

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value> & {
    placeholder?: string;
  }
>(({ className, placeholder = "Selecciona...", ...props }, ref) => {
  const { error, touched } = useContext(SelectErrorContext);

  return (
    <SelectPrimitive.Value
      ref={ref}
      className={cn(className)}
      placeholder={
        <span className={`${error && touched ? "text-de+1" : "text-bg+3"}`}>
          {placeholder}
        </span>
      }
      {...props}
    />
  );
});
SelectValue.displayName = SelectPrimitive.Value.displayName;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  Omit<
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    "onValueChange"
  > & {
    isLoading?: boolean;
    variant?: "default" | "destructive";
    error?: string;
    touched?: boolean;
    onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
    label?: string;
    labelClassName?: string;
    subLabelClassName?: string;
    subLabel?: string;
    icon?: React.ReactNode;
    labelTooltip?: string;
    classNameParent?: string;
    addButton?: React.ReactNode;
    name?: string;
    value?: string;
    hasBeenOpened?: boolean; // Accept hasBeenOpened as a prop
    showNavArrows?: boolean; // Add showNavArrows prop
    size?: ControlSizeProp;
  }
>(
  (
    {
      className,
      classNameParent,
      children,
      isLoading,
      variant,
      error,
      label,
      labelClassName,
      subLabelClassName,
      subLabel,
      icon,
      labelTooltip,
      addButton,
      onBlur,
      value,
      touched,
      hasBeenOpened,
      showNavArrows = false,
      size,
      ...props
    },
    ref,
  ) => {
    const selectTriggerRef = useRef<HTMLDivElement | null>(null); // Create a ref for the date picker
    // Use Radix Select context for value change
    // type RadixSelectContextType =
    //   | { onValueChange?: (value: string) => void }
    //   | undefined;

    // const createSyntheticBlurEvent = () => {
    //   if (onBlur) {
    //     // Create a minimal synthetic event if none is provided
    //     const syntheticEvent = {
    //       target: {
    //         name: name || "", // Add the name prop from your DatePicker props
    //         value: value,
    //       },
    //       currentTarget: selectTriggerRef.current,
    //       relatedTarget: null,
    //       nativeEvent: new Event("blur"),
    //       type: "blur",
    //       bubbles: true,
    //       cancelable: true,
    //       defaultPrevented: false,
    //       eventPhase: 0,
    //       isTrusted: true,
    //       timeStamp: Date.now(),
    //       persist: () => {},
    //       isDefaultPrevented: () => false,
    //       isPropagationStopped: () => false,
    //     } as unknown as React.FocusEvent<HTMLButtonElement>;
    //     if (hasBeenOpened && !value) {
    //       onBlur(syntheticEvent);
    //     }
    //   }
    // };

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          selectTriggerRef.current &&
          !selectTriggerRef.current.contains(event.target as Node) &&
          !(event.target as Element).closest('[role="listbox"]') // avoids conflict with calendar
        ) {
          // Only mark as touched (blur) if the select was opened at least once
          if (hasBeenOpened && onBlur) {
            // Create a synthetic event with proper name attribute for Formik
            const syntheticEvent = {
              ...event,
              target: {
                ...event.target,
                name: props.name || "",
                id: props.id || "",
              },
              currentTarget: selectTriggerRef.current,
            } as unknown as React.FocusEvent<HTMLButtonElement>;
            onBlur(syntheticEvent);
          }
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [hasBeenOpened, onBlur, props.name, props.id]);

    // Navigation logic for up/down arrows
    const { items } = useContext(SelectItemsContext);
    const onValueChangeFromContext = useContext(SelectValueChangeContext);
    const handleNav = (direction: "up" | "down") => {
      if (!items || !items.length) return;
      const currentIndex = items.findIndex((v) => v === value);
      if (currentIndex === -1) return;
      let newIndex;
      if (direction === "up") {
        newIndex = (currentIndex - 1 + items.length) % items.length;
      } else {
        newIndex = (currentIndex + 1) % items.length;
      }
      onValueChangeFromContext(items[newIndex]);
    };

    return (
      <SelectErrorContext.Provider
        value={{ error: !!error, touched: !!touched }}
      >
        <div
          className={cn("flex flex-col relative", classNameParent)}
          ref={selectTriggerRef}
        >
          <CustomLabel
            label={label}
            labelClassName={labelClassName}
            subLabel={subLabel}
            subLabelClassName={subLabelClassName}
            labelTooltip={labelTooltip}
            icon={icon}
          />

          {/* Trigger + Nav Arrows */}
          <div className="flex items-center w-full gap-1">
            {/* Trigger */}
            <div className="flex-1 overflow-hidden relative">
              <SelectPrimitive.Trigger
                ref={ref}
                className={cn(
                  "group/select flex w-full items-center justify-between cursor-pointer",
                  inputVariants({
                    variant: error && touched ? "destructive" : variant,
                    size,
                  }),
                  // isLoading && "cursor-not-allowed bg-bg+1 text-fg-muted",
                  className,
                )}
                {...props}
                disabled={isLoading || props.disabled}
              >
                {/* Selected label — will truncate if needed */}
                <span className="flex-1 min-w-0 truncate text-left flex items-center">
                  {children}
                </span>

                {/* Chevron or Spinner — always visible */}
                <SelectPrimitive.Icon asChild className="ml-1 flex-shrink-0">
                  {isLoading ? (
                    <Spinner className="opacity-50" size={16} />
                  ) : (
                    <LuChevronsUpDown className="opacity-50" />
                  )}
                </SelectPrimitive.Icon>
              </SelectPrimitive.Trigger>
            </div>

            {/* Optional Add Button */}
            {addButton}

            {/* Nav Arrows — always visible and not squeezed */}
            {showNavArrows && (
              <div className="flex-shrink-0 flex gap-1 mr-1">
                <Button
                  type="button"
                  variant="outline"
                  aria-label="Previous"
                  className="p-0.5 hover:bg-bg rounded"
                  onClick={() => handleNav("up")}
                  tabIndex={-1}
                >
                  <ChevronUpIcon className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  aria-label="Next"
                  className="p-0.5 hover:bg-bg rounded"
                  onClick={() => handleNav("down")}
                  tabIndex={-1}
                >
                  <ChevronDownIcon className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Error message */}
          {error && touched && (
            <p className="text-xs text-de+2 mt-1 font-normal text-left">
              {error}
            </p>
          )}
        </div>
      </SelectErrorContext.Provider>
    );
  },
);
SelectTrigger.displayName = "SelectTrigger";

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUpIcon />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDownIcon />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => {
  // CRITICAL: Cleanup portal on unmount to prevent DOM errors during navigation
  useEffect(() => {
    return () => {
      // Cleanup portal when component unmounts
      if (typeof document !== "undefined") {
        requestAnimationFrame(() => {
          const portals = document.querySelectorAll("[data-radix-portal]");
          portals.forEach((portal) => {
            const hasSelectContent = portal.querySelector('[role="listbox"]');
            if (
              hasSelectContent &&
              (portal as HTMLElement).offsetParent === null
            ) {
              // Portal is hidden, safe to remove
              try {
                portal.remove();
              } catch {
                // Silently ignore - portal may already be removed
              }
            }
          });
        });
      }
    };
  }, []);

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          `relative 
        z-[250] 
        max-h-96 
        min-w-[8rem] 
        max-w-[calc(100vw-1rem)] 
        overflow-hidden 
        rounded-md border 
        bg-popover 
        text-popover-foreground 
        shadow-md
        `,
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        style={{
          backgroundColor: "white",
          WebkitTapHighlightColor: "transparent",
          ...props.style,
        }}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1 bg-white w-full max-w-full overflow-x-hidden",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-1rem)]",
          )}
          style={{
            backgroundColor: "white",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => {
  const { register, unregister } = useContext(SelectItemsContext);
  useEffect(() => {
    register(props.value, props.disabled);
    return () => unregister(props.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, props.disabled]);

  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        `relative 
        flex 
        w-full 
        cursor-pointer 
        select-none 
        items-center 
        rounded-sm 
        py-1.5 
        pl-2 
        pr-8 
        text-base
        lg:text-sm 
        outline-none 
        focus:bg-bg 
        focus:text-accent-foreground 
        data-[disabled]:pointer-events-none 
 
        hover:bg-bg
        active:bg-bg`,
        className,
      )}
      style={
        {
          WebkitTapHighlightColor: "transparent",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          ...props.style,
        } as React.CSSProperties
      }
      {...props}
    >
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
